/**
 * Two-tier cache manager for user photos with memory and localStorage support.
 * - Implements LRU eviction, memory pressure monitoring, and TTL-based expiration.
 * - Optimizes photo loading by maintaining frequently accessed data in memory
 * - while persisting to localStorage for session continuity.
 */
export default class CacheManager {
  /**
   * Creates a new CacheManager instance with configurable cache behavior.
   *
   * @param {Object} [config={}] - Cache configuration options
   * @param {number} [config.maxMemoryEntries=1000] - Maximum entries in memory cache
   * @param {number} [config.photoTtl=86400000] - TTL for photo data in milliseconds (24 hours)
   * @param {number} [config.noPhotoTtl=300000] - TTL for "no photo" entries in milliseconds (5 minutes)
   * @param {string} [config.localStoragePrefix='userPhoto_'] - Prefix for localStorage keys
   * @param {number} [config.memoryPressureThreshold=0.8] - Memory usage threshold for cleanup (0-1)
   */
  constructor(config) {
    this.memoryCache = new Map();

    this.config = {
      maxMemoryEntries: 1000,
      photoTtl: 24 * 60 * 60 * 1000,
      noPhotoTtl: 5 * 60 * 1000,
      localStoragePrefix: 'userPhoto_',
      memoryPressureThreshold: 0.8,
      ...config,
    };

    this.metrics = { hits: 0, misses: 0, evictions: 0 };
  }

  /**
   * Retrieves data from cache, checking memory first then localStorage.
   * Updates memory cache when retrieving from localStorage (cache promotion).
   *
   * @param {string} cacheKey - Unique identifier for the cached item
   * @returns {*|null} Cached data if found and valid, null otherwise
   */
  get(cacheKey) {
    const memoryCached = this._getFromMemory(cacheKey);
    if (memoryCached && this._isValidCache(memoryCached)) {
      this.metrics.hits++;
      return memoryCached.data;
    }

    const localCached = this._getFromLocalStorage(cacheKey);

    if (localCached && this._isValidCache(localCached)) {
      this.metrics.hits++;
      this._setInMemory(cacheKey, localCached);
      return localCached.data;
    }

    this.metrics.misses++;
    return null;
  }

  /**
   * Stores data in both memory and localStorage caches with TTL.
   * Temporary entries (like "no photo" indicators) are only stored in memory.
   *
   * @param {string} cacheKey - Unique identifier for the cache entry
   * @param {*} data - Data to cache (typically Blob for photos)
   * @param {boolean} [isTemporary=false] - If true, only stores in memory with shorter TTL
   * @returns {void}
   */
  set(cacheKey, data, isTemporary = false) {
    const ttl = isTemporary ? this.config.noPhotoTtl : this.config.photoTtl;
    const expiration = new Date(Date.now() + ttl).toISOString();
    const cacheEntry = { data, expiration };

    this._setInMemory(cacheKey, cacheEntry);
    if (!isTemporary) {
      this._setInLocalStorage(cacheKey, cacheEntry);
    }
  }

  invalidate(cacheKey) {
    this.memoryCache.delete(cacheKey);
    this._removeFromLocalStorage(cacheKey);
  }

  /**
   * Clears all cached data from memory and localStorage, resets metrics.
   * Use carefully as this will force re-fetching of all photos.
   */
  clear() {
    this.memoryCache.clear();
    this._clearLocalStorage();
    this.metrics = { hits: 0, misses: 0, evictions: 0 };
  }

  /**
   * Monitors memory usage and triggers cleanup if pressure exceeds threshold.
   * Uses performance.memory API when available to detect memory pressure.
   *
   * @returns {number} Current memory pressure ratio (0-1), or 0 if unavailable
   */
  checkMemoryPressure() {
    if (typeof performance !== 'undefined' && 'memory' in performance) {
      const memory = performance.memory;
      const pressure = memory.usedJSHeapSize / memory.jsHeapSizeLimit;

      if (pressure > this.config.memoryPressureThreshold) {
        this._performMemoryCleanup();
      }

      return pressure;
    }

    return 0;
  }

  /**
   * Removes expired entries from memory cache during idle time.
   * Should be called periodically to prevent memory bloat.
   *
   * @returns {number} Number of entries removed from memory cache
   */
  performBackgroundCleanup() {
    let removedCount = 0;

    for (const [key, entry] of this.memoryCache.entries()) {
      if (!this._isValidCache(entry)) {
        this.memoryCache.delete(key);
        removedCount++;
      }
    }

    return removedCount;
  }

  getMetrics() {
    return {
      ...this.metrics,
      cacheSize: this.memoryCache.size,
    };
  }

  /**
   * Validates if a cache entry is still valid based on expiration time.
   *
   * @private
   * @param {Object} cacheEntry - Cache entry to validate
   * @param {string} cacheEntry.expiration - ISO timestamp of expiration
   * @returns {boolean} True if entry is valid and not expired
   */
  _isValidCache(cacheEntry) {
    if (!cacheEntry || !cacheEntry.expiration) return false;
    const expirationTime = new Date(cacheEntry.expiration).getTime();
    return !isNaN(expirationTime) && Date.now() <= expirationTime;
  }

  /**
   * Retrieves entry from memory cache.
   *
   * @private
   * @param {string} cacheKey - Key to retrieve
   * @returns {Object|undefined} Cache entry or undefined if not found
   */
  _getFromMemory(cacheKey) {
    return this.memoryCache.get(cacheKey);
  }

  /**
   * Stores entry in memory cache with LRU eviction when at capacity.
   * Evicts 10% of oldest entries when memory limit is reached.
   *
   * @private
   * @param {string} cacheKey - Key to store under
   * @param {Object} cacheEntry - Entry to store
   */
  _setInMemory(cacheKey, cacheEntry) {
    if (this.memoryCache.size >= this.config.maxMemoryEntries) {
      const entriesToRemove = Math.max(1, Math.floor(this.config.maxMemoryEntries * 0.1));
      const keys = Array.from(this.memoryCache.keys());

      for (let i = 0; i < entriesToRemove && keys[i]; i++) {
        this.memoryCache.delete(keys[i]);
        this.metrics.evictions++;
      }
    }

    if (this.memoryCache.has(cacheKey)) {
      this.memoryCache.delete(cacheKey);
    }

    this.memoryCache.set(cacheKey, {
      ...cacheEntry,
      accessTime: Date.now(),
    });
  }

  /**
   * Retrieves and validates entry from localStorage.
   * Handles JSON parsing errors and removes corrupted entries.
   *
   * @private
   * @param {string} cacheKey - Key to retrieve
   * @returns {Object|null} Parsed cache entry or null if not found/invalid
   */
  _getFromLocalStorage(cacheKey) {
    try {
      const storageKey = this.config.localStoragePrefix + cacheKey;
      const stored = localStorage.getItem(storageKey);

      if (!stored) return null;
      const parsed = JSON.parse(stored);

      if (!parsed?.data || parsed.userId !== cacheKey || !parsed.expiration) {
        localStorage.removeItem(storageKey);
        return null;
      }

      return parsed;
    } catch (error) {
      try {
        localStorage.removeItem(this.config.localStoragePrefix + cacheKey);
      } catch (e) {}
      return null;
    }
  }

  /**
   * Stores entry in localStorage with quota exceeded error handling.
   * Triggers storage cleanup on quota exceeded and retries once.
   *
   * @private
   * @param {string} cacheKey - Key to store under
   * @param {Object} cacheEntry - Entry to store
   */
  _setInLocalStorage(cacheKey, cacheEntry) {
    try {
      const entryWithUserId = { ...cacheEntry, userId: cacheKey };
      const storageKey = this.config.localStoragePrefix + cacheKey;
      localStorage.setItem(storageKey, JSON.stringify(entryWithUserId));
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        this._performStorageCleanup();
        try {
          const entryWithUserId = { ...cacheEntry, userId: cacheKey };
          const storageKey = this.config.localStoragePrefix + cacheKey;
          localStorage.setItem(storageKey, JSON.stringify(entryWithUserId));
        } catch (e) {}
      }
    }
  }

  _removeFromLocalStorage(cacheKey) {
    try {
      localStorage.removeItem(this.config.localStoragePrefix + cacheKey);
    } catch (e) {}
  }

  _clearLocalStorage() {
    try {
      const keysToRemove = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(this.config.localStoragePrefix)) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach((key) => {
        try {
          localStorage.removeItem(key);
        } catch (e) {}
      });
    } catch (e) {}
  }

  /**
   * Performs aggressive memory cleanup by removing half of the cache entries.
   * Prioritizes removing least recently accessed items first.
   * @private
   */
  _performMemoryCleanup() {
    const entries = Array.from(this.memoryCache.entries());
    entries.sort((a, b) => (a[1].accessTime || 0) - (b[1].accessTime || 0));
    const toRemove = Math.floor(entries.length / 2);

    for (let i = 0; i < toRemove; i++) {
      this.memoryCache.delete(entries[i][0]);
      this.metrics.evictions++;
    }
  }

  /**
   * Removes oldest localStorage entries when quota is exceeded.
   * Sorts by expiration time and removes oldest half of entries.
   * @private
   */
  _performStorageCleanup() {
    try {
      const entries = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(this.config.localStoragePrefix)) {
          try {
            const value = localStorage.getItem(key);
            const parsed = JSON.parse(value);
            const expirationTime = parsed.expiration ? new Date(parsed.expiration).getTime() : 0;
            entries.push({ key, expiration: expirationTime });
          } catch (e) {
            entries.push({ key, expiration: 0 });
          }
        }
      }

      entries.sort((a, b) => a.expiration - b.expiration);
      const toRemove = Math.floor(entries.length / 2);

      for (let i = 0; i < toRemove; i++) {
        try {
          localStorage.removeItem(entries[i].key);
        } catch (e) {}
      }
    } catch (e) {}
  }
}
